/**
 * Shared Polyfills
 * Custom polyfill implementations used across the application.
 * These are lightweight, self-contained implementations that avoid external dependencies.
 *
 * Our browserslist targets Chrome 80+, Firefox 85+, Safari 14+, Edge 80+.
 * Chrome/Edge 80+ is required because Next.js 16.1.2+ has a Turbopack/SWC minification bug
 * where transforming nullish coalescing (`??`) to older syntax creates invalid JavaScript.
 * By targeting browsers that support `??` natively (Chrome 80+), we avoid this transformation.
 */

/**
 * Polyfill Array.prototype.findLast for Chrome 80-96, Firefox 85-103, Safari 14-15.3
 * Native support: Chrome 97+, Firefox 104+, Safari 15.4+
 */
export function polyfillArrayFindLast(): void {
  if (!Array.prototype.findLast) {
    Array.prototype.findLast = function <T>(
      this: T[],
      predicate: (value: T, index: number, array: T[]) => boolean,
    ): T | undefined {
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate(this[i], i, this)) {
          return this[i];
        }
      }
      return undefined;
    };
  }
}

/**
 * Polyfill Array.prototype.findLastIndex for Chrome 80-96, Firefox 85-103, Safari 14-15.3
 * Native support: Chrome 97+, Firefox 104+, Safari 15.4+
 */
export function polyfillArrayFindLastIndex(): void {
  if (!Array.prototype.findLastIndex) {
    Array.prototype.findLastIndex = function <T>(
      this: T[],
      predicate: (value: T, index: number, array: T[]) => boolean,
    ): number {
      for (let i = this.length - 1; i >= 0; i--) {
        if (predicate(this[i], i, this)) {
          return i;
        }
      }
      return -1;
    };
  }
}

/**
 * Polyfill TextDecoderStream for Firefox 85-104
 * Native support: Chrome 71+, Firefox 105+, Safari 14.1+
 * This Web API is not available in standard polyfill libraries.
 */
export function polyfillTextDecoderStream(): void {
  // Check if already exists globally
  if (typeof TextDecoderStream !== 'undefined') {
    return;
  }

  // Ensure TransformStream is available (required for polyfill)
  if (typeof TransformStream === 'undefined') {
    return;
  }

  class TextDecoderStreamPolyfill extends TransformStream {
    constructor(encoding = 'utf-8', options = {}) {
      const decoder = new TextDecoder(encoding, options);
      super({
        transform(chunk, controller) {
          controller.enqueue(decoder.decode(chunk, { stream: true }));
        },
        flush(controller) {
          const output = decoder.decode();
          if (output) {
            controller.enqueue(output);
          }
        },
      });
    }
  }

  // Set on all available global objects to ensure compatibility
  // In browsers: sets on window
  // In Node.js test environment: sets on global
  // In JSDOM: sets on both window and global
  if (typeof window !== 'undefined') {
    window.TextDecoderStream = TextDecoderStreamPolyfill as unknown as typeof TextDecoderStream;
  }
  if (typeof global !== 'undefined') {
    global.TextDecoderStream = TextDecoderStreamPolyfill as unknown as typeof TextDecoderStream;
  }
}

/**
 * Polyfill structuredClone for Chrome 80-97, Firefox 85-93, Safari 14-15.3
 * Native support: Chrome 98+, Firefox 94+, Safari 15.4+
 * Used by AI SDK to deep-clone message objects during streaming.
 * JSON round-trip is sufficient since AI SDK only clones serializable message objects.
 */
export function polyfillStructuredClone(): void {
  if (typeof structuredClone !== 'undefined') {
    return;
  }

  globalThis.structuredClone = (<T>(obj: T): T =>
    JSON.parse(JSON.stringify(obj))) as typeof structuredClone;
}

let compositionEndPatched = false;

/**
 * Workaround for https://github.com/facebook/react/issues/24989 (unfixed as of React 19;
 * community fix PR https://github.com/facebook/react/pull/31375 was closed without merging).
 *
 * React's `getDataFromCustomEvent` checks `typeof detail === 'object'` before
 * doing `'data' in detail`, but `typeof null === 'object'` passes that guard,
 * so any third-party code that dispatches `new CustomEvent('compositionend')`
 * without an explicit `detail` (which defaults to null) crashes React with
 * "Cannot use 'in' operator to search for 'data' in null".
 * Real browser IME compositionend always has `detail = 0` (a UIEvent long),
 * never null — so intercepting null-detail events in capture phase is safe.
 */
export function suppressNullDetailCompositionEnd(): void {
  if (typeof document === 'undefined' || compositionEndPatched) return;
  compositionEndPatched = true;
  document.addEventListener(
    'compositionend',
    (event: Event) => {
      if ((event as CustomEvent).detail === null) {
        event.stopImmediatePropagation();
      }
    },
    true,
  );
}

/**
 * Polyfill AbortSignal.any() for Chrome 80-115, Firefox 85-123, Safari 14-17.3
 * Native support: Chrome 116+, Firefox 124+, Safari 17.4+
 * Creates a combined AbortSignal that aborts when any input signal aborts.
 */
export function polyfillAbortSignalAny(): void {
  if (typeof AbortSignal?.any === 'function') {
    return;
  }

  if (typeof AbortSignal === 'undefined') {
    return;
  }

  AbortSignal.any = function (signals: Iterable<AbortSignal>): AbortSignal {
    const controller = new AbortController();
    const signalArray = Array.from(signals);

    for (const signal of signalArray) {
      if (signal.aborted) {
        controller.abort(signal.reason);
        return controller.signal;
      }
    }

    for (const signal of signalArray) {
      signal.addEventListener(
        'abort',
        () => {
          if (!controller.signal.aborted) {
            controller.abort(signal.reason);
          }
        },
        { once: true },
      );
    }

    return controller.signal;
  };
}

/**
 * Polyfill AbortSignal.timeout() for Chrome 80-102, Firefox 85-99, Safari 14-15.
 * Native support: Chrome 103+, Firefox 100+, Safari 16+
 * Aborts with a TimeoutError after the given number of milliseconds.
 */
export function polyfillAbortSignalTimeout(): void {
  if (typeof AbortSignal?.timeout === 'function') {
    return;
  }

  if (typeof AbortSignal === 'undefined') {
    return;
  }

  AbortSignal.timeout = function (milliseconds: number): AbortSignal {
    const controller = new AbortController();
    setTimeout(
      () => controller.abort(new DOMException('signal timed out', 'TimeoutError')),
      milliseconds,
    );
    return controller.signal;
  };
}
